(ns ui.editors.entity-creator.sub
  (:require
   [re-frame.core :as rf]
   [reagent.ratom :as ratom]
   [common.localization :as loc]
   [common.permissions.field-permissions :as field-permissions]
   [common.schema.associations :as associations]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.entity-type-names :as entity-type-names]
   [common.schema.registry :as registry]
   [frontend.state.current-session :as session]
   [ui.state.session.sub :as session-sub]
   [ui.editors.entity-creator.model :as m]
   [ui.editors.entity-creator.utils :as u]
   [common.util :as cu]))

(rf/reg-sub
 m/state
 (fn [db _]
   (get db m/root)))

(rf/reg-sub
 m/open?
 :<- [m/state]
 (fn [state]
   (get state m/open-key)))

(rf/reg-sub
 m/saving?
 :<- [m/state]
 (fn [state]
   (get state m/saving-key)))

(rf/reg-sub
 m/pipeline
 :<- [m/state]
 (fn [state]
   (get state m/pipeline-key)))

(rf/reg-sub
 m/pipeline-name
 :<- [m/pipeline]
 ;; avoid shadowing `clojure.core/name` function vs. clj-kondo warning:
 #_{:clj-kondo/ignore [:redundant-fn-wrapper]}
 (fn [pipeline]
   (:pipeline/name pipeline)))

(defn stages-reaction
  [_db-atom]
  (ratom/reaction
   (->> @(rf/subscribe [m/pipeline])
        (session/pipeline-stages)
        (remove :common/disabled?)
        (sort-by :common/ord)
        doall
        seq)))

(rf/reg-sub-raw
 m/stages
 #'stages-reaction)

(defn system-mandatory-reaction
  [_db-atom [_ {:keys [entity-type pipeline]}]]
  (ratom/reaction
   (let [tenant @(rf/subscribe [::session-sub/tenant])]
     (->> (ecu/mandatory-fields
           {:tenant tenant
            :pipeline pipeline
            :entity-type entity-type
            :tenant-schema @session/tenant-schema
            :field-configs (:tenant/field-configurations
                            tenant)
            :tenant-modules @session/tenant-modules} entity-type)
          keys
          set))))

(rf/reg-sub-raw
 m/system-mandatory
 #'system-mandatory-reaction)

(rf/reg-sub
 m/stages-config
 (fn [[_ {:keys [entity-type]}]]
   {:stages (rf/subscribe [m/stages])
    :system-mandatory (rf/subscribe [m/system-mandatory
                                     {:entity-type entity-type}])})
 (fn [{:keys [system-mandatory
              stages]}
      [_event-id {:keys [field-config]}]]
   (ecu/resolve-stage-config
    {:system-mandatory system-mandatory
     :stages stages
     :field-config field-config})))

(rf/reg-sub
 m/association-stages-config
 (fn [_]
   {:stages (rf/subscribe [m/stages])})
 (fn [{:keys [stages]}
      [_event-id {:keys [association-config]}]]
   (ecu/resolve-association-stage-config
    {:stages stages
     :association-config association-config})))

(rf/reg-sub
 m/create-config
 (fn [[_ {:keys [entity-type]}]]
   {:system-mandatory (rf/subscribe [m/system-mandatory
                                     {:entity-type entity-type}])})
 (fn [{:keys [system-mandatory]}
      [_event-id {:keys [field-config]}]]
   (ecu/resolve-create-config
    {:system-mandatory system-mandatory
     :field-config field-config})))

(rf/reg-sub
 m/entity-type
 :<- [m/state]
 (fn [state]
   (get state m/entity-type-key)))

(rf/reg-sub
 m/target-type
 :<- [m/state]
 (fn [state]
   (get state m/target-type-key)))

(rf/reg-sub
 m/edits
 :<- [m/state]
 (fn [state]
   (get state m/edits-key)))

(rf/reg-sub
 m/edited-config
 :<- [m/edits]
 (fn [edits [_ {:keys [config-path]}]]
   (get-in edits config-path)))

(rf/reg-sub
 m/title
 :<- [m/pipeline-name]
 :<- [m/entity-type]
 :<- [m/target-type]
 (fn [[pipeline-name entity-type target-type]]
   (let [entity-type-name (entity-type-names/get-name entity-type :en)
         target-type-name (when target-type
                            (entity-type-names/get-name target-type :en))
         title (cond (and entity-type pipeline-name target-type)
                     (loc/tr :creator-config/field-association-requirements)

                     ;; with pipeline, no target (i.e. froppo)
                     (and entity-type pipeline-name)
                     (loc/tr :creator-config/field-association-requirements)

                     ;; no pipeline, with target (i.e. holding)
                     ;; TODO ensure this case is needed
                     (and entity-type target-type)
                     (loc/tr :entity-creator-config-target-no-pipeline
                         [entity-type-name target-type-name])

                     entity-type
                     (loc/tr :entity-creator-config-no-pipeline
                         [entity-type-name])

                     :else
                     "Error: no entity-type")]
     title)))

(defn resolve-entity-config
  [{:keys [config stages entity-config-path entity-type system-mandatory]}]
  (let [entity-config (get-in config entity-config-path)
        config-fn (if stages
                    (fn [attribute setting]
                      {:attribute attribute
                       :stages
                       (->> stages
                            (mapv (fn [{:keys [db/id]}]
                                    [id {:setting
                                         setting}]))
                            (into {}))})
                    (fn [attribute setting]
                      {:attribute attribute
                       :create {:setting setting}}))
        entity-config (or (not-empty entity-config)
                          (ecu/default-config
                           {:config-fn config-fn
                            :entity-type entity-type}))
        config-attributes (->> entity-config
                               :fields
                               (mapv :attribute)
                               set)
        config-mandatory-fn #(config-fn % :mandatory)
        missing-mandatory (->> system-mandatory
                               (remove (fn [a]
                                         (contains? config-attributes a)))
                               (mapv config-mandatory-fn))
        entity-config (if (seq missing-mandatory)
                        (update entity-config :fields
                                (fn [fields]
                                  (into missing-mandatory fields)))
                        entity-config)]
    entity-config))

(rf/reg-sub-raw
 m/changes?
 (fn [_db-atom _]
   (ratom/reaction
    (let [pipeline @(rf/subscribe [m/pipeline])
          tenant @(rf/subscribe [::session-sub/tenant])
          entity-type @(rf/subscribe [m/entity-type])
          target-type @(rf/subscribe [m/target-type])
          edits @(rf/subscribe [m/edits])
          stages @(rf/subscribe [m/stages])
          et-system-mandatory @(rf/subscribe [m/system-mandatory
                                              {:entity-type entity-type
                                               :pipeline pipeline}])
          tt-system-mandatory (when target-type
                                @(rf/subscribe [m/system-mandatory
                                                {:entity-type target-type
                                                 :pipeline pipeline}]))

          {:keys [main-config-path
                  target-config-path
                  target-profile-config-path
                  associations-config-path]} (ecu/resolve-config-paths
                                              {:entity-type entity-type
                                               :target-type target-type
                                               :pipeline pipeline})
          tp-type (ecu/resolve-target-profile-type
                   {:pipeline pipeline
                    :entity-type entity-type
                    :target-type target-type})
          tp-system-mandatory (when tp-type
                                @(rf/subscribe [m/system-mandatory
                                                {:entity-type tp-type
                                                 :pipeline pipeline}]))
          entity-config-path main-config-path
          et-edits (get-in edits entity-config-path)
          tt-edits (when target-type
                     (get-in edits target-config-path))
          tp-edits (when tp-type
                     (get-in edits target-profile-config-path))
          associations-edits (get-in edits associations-config-path)

          config (if pipeline
                   (:pipeline/creator-config pipeline)
                   (:tenant/entity-creation-config tenant))
          et-config (resolve-entity-config
                     {:config config
                      :stages stages
                      :entity-type entity-type
                      :entity-config-path entity-config-path
                      :system-mandatory et-system-mandatory})
          tt-config (when target-type
                      (resolve-entity-config
                       {:config config
                        :stages stages
                        :entity-type target-type
                        :entity-config-path target-config-path
                        :system-mandatory tt-system-mandatory}))
          tp-config (when tp-type
                      (resolve-entity-config
                       {:config config
                        :stages stages
                        :entity-type tp-type
                        :entity-config-path target-profile-config-path
                        :system-mandatory tp-system-mandatory}))
          associations-config (get-in config associations-config-path)
          et-changes? (not= et-config et-edits)
          tt-changes? (when target-type
                        (not= tt-config tt-edits))
          tp-changes? (when tp-type
                        (not= tp-config tp-edits))
          association-changes? (not= associations-config associations-edits)]
      (or et-changes?
          tt-changes?
          tp-changes?
          association-changes?)))))

;; TODO remove if not needed
(rf/reg-sub
 m/config
 :<- [m/entity-type]
 :<- [m/target-type]
 :<- [m/pipeline]
 :<- [::session-sub/tenant]
 (fn [[entity-type target-type pipeline tenant]]
   (let [full-config (if pipeline
                       (:pipeline/creator-config pipeline)
                       (:tenant/entity-creation-config tenant))
         entity-config (if (not target-type)
                         (get full-config entity-type)
                         (get-in full-config [target-type entity-type]))]
     entity-config)))

(rf/reg-sub-raw
 m/available-fields
 (fn [_db-atom [_ {:keys [pipeline entity-type exclude-attributes]}]]
   (ratom/reaction
    (let [tenant @(rf/subscribe [::session-sub/tenant])]
      (ecu/configurable-creator-fields
               {:pipeline pipeline
                :tenant tenant
                :entity-type entity-type
                :tenant-schema @session/tenant-schema
                :field-configs (:tenant/field-configurations tenant)
                :exclude-attributes exclude-attributes}
       entity-type)))))

(defn person-association-options
  [{:keys [only-entity-type?]} a-source-type]
  (let [ats (get-in associations/schema [a-source-type :entity.type/person])]
    (->> ats
         (mapv (fn [[association-type _]]
                 ;; [source-entity-type target-entity-type association-type]
                 {:value [a-source-type :entity.type/person association-type]
                  :label (u/association-label
                          {:only-entity-type? only-entity-type?}
                          a-source-type
                          association-type)})))))

(rf/reg-sub-raw
 m/available-association-options
 (fn [_db-atom _]
   (ratom/reaction
    (let [;;tenant @(rf/subscribe [::session-sub/tenant])
          et @(rf/subscribe [m/entity-type])
          tt @(rf/subscribe [m/target-type])
          pipeline @(rf/subscribe [m/pipeline])
          a-source-types (->> [et
                               (ecu/resolve-target-profile-type
                                {:pipeline pipeline
                                 :entity-type et
                                 :target-type tt})
                               tt]
                              (remove nil?))
          only-entity-type? (cu/one-element? a-source-types)]
      (->> a-source-types
           (mapcat (partial person-association-options
                            {:only-entity-type? only-entity-type?}))
           vec)))))

(defn team-ids-no-access
  [cfg]
  (->> cfg
       (filterv (fn [[k _v]]
                  (-> k
                      namespace
                      (= field-permissions/TEAM_PERMISSIONS_NAMESPACE))))
       (remove (fn [[_k v]]
                 (field-permissions/write-access? v)))
       (mapv (fn [[k _v]]
               (field-permissions/parse-team-id k)))))

;; returns map of team-id => vector that contains :global-field-access
;;  if team has no write-access for attribute in field-settings, and
;;  :pipeline-field-settings if in pipeline-specific field-settings:
(rf/reg-sub-raw
 m/teams-without-write-access
 (fn [_db-atom [_ {:keys [pipeline entity-type attribute]}]]
   (ratom/reaction
    (let [tenant @(rf/subscribe [::session-sub/tenant])
          s-pipeline @(rf/subscribe [m/pipeline])
          pipeline (or pipeline s-pipeline)
          pipeline-team-ids (some->> pipeline
                                     :pipeline/teams
                                     (mapv (fn [id]
                                             @(rf/subscribe [::session-sub/entity id])))
                                     (remove :common/disabled?)
                                     (mapv :db/id)
                                     set)
          ;; note: in backend, pipeline-settings are applied only for
          ;; pipeline-entity-types:
          ;; => asset-types are configurable via pipeline-field-settings, but
          ;;    ignored in backend - how should they actually work?
          pipeline-field-configs (:pipeline/settings pipeline)
          pipeline-cfg (get-in pipeline-field-configs [entity-type attribute])
          tenant-field-configs (:tenant/field-configurations tenant)
          tenant-cfg (get-in tenant-field-configs [entity-type attribute])
          teams-global (->> (team-ids-no-access tenant-cfg)
                            (filterv (fn [id]
                                       (if pipeline
                                         (contains? pipeline-team-ids id)
                                         true)))
                            (mapv (fn [id]
                                    [id [:global-field-settings]]))
                            (into {}))
          teams-pipeline (->> (team-ids-no-access pipeline-cfg)
                              (filterv (fn [id]
                                         (if pipeline
                                           (contains? pipeline-team-ids id)
                                           true)))
                              (mapv (fn [id]
                                      [id [:pipeline-field-settings]]))
                              (into {}))]
      (merge-with into teams-global teams-pipeline)))))

(rf/reg-sub-raw
 m/inactive-field
 (fn [_db-atom [_ {:keys [pipeline entity-type attribute]}]]
   (ratom/reaction
    (let [tenant @(rf/subscribe [::session-sub/tenant])
          blacklisted? (contains? (:tenant/blacklisted tenant)
                                  attribute)
          tenant-field-configs (:tenant/field-configurations tenant)
          tenant-cfg (get-in tenant-field-configs [entity-type attribute])
          tenant-schema @session/tenant-schema
          entity-schema (registry/get-schema tenant-schema entity-type)
          field-schema (get entity-schema attribute)
          {:keys [field/default-visibility]} field-schema
          default-field-visibility (:field-visibility default-visibility)
          tenant-field-setting (or (:field-settings/in-use-config tenant-cfg)
                                   default-field-visibility
                                   :not-in-use)
          inactive-for-tenant? (contains? field-permissions/NEGATIVE-IN-USE-VALUES
                                          tenant-field-setting)
          pipeline (or pipeline @(rf/subscribe [m/pipeline]))
          pipeline-cfg (some-> pipeline
                               :pipeline/settings
                               (get-in [entity-type attribute]))
          pipeline-field-setting (or (:field-settings/in-use-config pipeline-cfg)
                                     tenant-field-setting)
          inactive-for-pipeline? (contains? field-permissions/NEGATIVE-IN-USE-VALUES
                                            pipeline-field-setting)]
      (cond (and (not blacklisted?)
                 (not inactive-for-tenant?)
                 inactive-for-pipeline?)
            :inactive-for-pipeline

            blacklisted?
            :blacklisted

            inactive-for-tenant?
            :inactive

            :else
            nil)))))
